import { storeToRefs } from "pinia";
import type {
	ParametricListValue,
	Product
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/*
	One in-flight catalog request per locale, shared by every caller, so
	components that ask at the same time (colour swatches, colour families,
	quick buy) cause one request, not one each. Not on the server: a
	module-level promise there would be shared between visitors' requests.
*/
const pendingRequests = new Map<string, Promise<ParametricListValue[]>>();

export const useAllParametrics = () => {
	const { getAllParametrics } = useNorcePwa();
	const { allParametrics } = storeToRefs(useNorceStore());

	const { locale } = useI18n();

	const fetchAllParametrics = async ({ force = false } = {}) => {
		const key = locale.value;
		const cached =
			allParametrics.value[key as keyof typeof allParametrics.value];
		if (!force && cached?.length) return cached;

		const shared = !import.meta.server;
		const inFlight = shared ? pendingRequests.get(key) : undefined;
		if (inFlight) return inFlight;

		const request = getAllParametrics({
			...useNorceStore().storeContext
		}).then((parametrics) => {
			useNorceStore().addAllParametrics({ [key]: parametrics });
			return parametrics;
		});

		if (shared) {
			pendingRequests.set(key, request);
			const release = () => pendingRequests.delete(key);
			request.then(release, release);
		}

		return request;
	};

	// groupParametrics silently drops a variant value when it can't be resolved
	// against allParametrics (e.g. a stale cached list from before a parametric
	// value was recreated in Norce with a new Id). Mirrors its matching rule:
	// parametric.ParametricId === variantParametric.Id and the value id matches
	// either ValueId or one of ValueIdSeed.
	const productParametricsResolved = (
		product: Product,
		parametrics: ParametricListValue[] | undefined
	): boolean => {
		if (!parametrics?.length) return false;

		const variantParametrics =
			product?.Variants?.flatMap(
				(variant) => variant.VariantParametrics ?? []
			) ?? [];

		return variantParametrics.every((variantParametric) => {
			const valueIds = [
				...(variantParametric.ValueId != null
					? [String(variantParametric.ValueId)]
					: []),
				...(variantParametric.ValueIdSeed
					? variantParametric.ValueIdSeed.split(",")
					: [])
			].filter(Boolean);

			if (!valueIds.length) return true;

			return parametrics.some(
				(parametric) =>
					parametric.ParametricId === variantParametric.Id &&
					valueIds.includes(String(parametric.Id))
			);
		});
	};

	// Returns a parametrics list that covers the product's variant values. If the
	// (possibly stale, localStorage-restored) list can't resolve them, refetches
	// once — self-heals without looping. Client-only: on SSR the list is always
	// fetched fresh within the same request, so a retry can't return anything new.
	const ensureParametricsForProduct = async (
		product: Product,
		parametrics: ParametricListValue[] | undefined
	): Promise<ParametricListValue[] | undefined> => {
		if (
			!import.meta.client ||
			productParametricsResolved(product, parametrics)
		) {
			return parametrics;
		}

		return fetchAllParametrics({ force: true });
	};

	return {
		fetchAllParametrics,
		ensureParametricsForProduct,
		productParametricsResolved
	};
};
